import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { queryTestExecutions } from '@/features/executions/data';
import { queryKeys, useDelayedFlag } from '@/shared/hooks';
import { iconSizes, s } from '@/theme/tokens';
import { DataStateSkeleton } from '@/ui/composites/DataStateSkeleton';
import { buildEqFilterValue, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Inline } from '@/ui/layout/Inline';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Chip,
  ExpandIcon,
  formatStatusLabel,
  getStatusTextColor,
  Link,
  Surface,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { buildTestDetails, useCopyPanel } from './resultsFileDetailUtils';
import {
  buildExecutionMetadataGridTemplate,
  EXECUTION_ROW_COLUMN_GAP,
  LATEST_CHIP_COLUMN_SX,
  measureOutputPanelWidthPx,
} from './resultsFileLayout';
import { ResultsFileOutputPanel } from './ResultsFileOutputPanel';
import type { TestExecutionDto } from '@flowqa/shared';

export type ResultsFileTestExecutionInlineListProps = {
  suiteExecutionId: string;
  resultsFileId: string | null;
  projectId: string;
  organisationId: string | null;
  heading?: string | null;
  disableIndent?: boolean;
  showExpandCollapseControls?: boolean;
  rowGridTemplate?: string;
  rowColumnGap?: string;
};

export const ResultsFileTestExecutionInlineList = ({
  suiteExecutionId,
  resultsFileId,
  projectId,
  organisationId,
  heading = null,
  disableIndent = false,
  showExpandCollapseControls = false,
  rowGridTemplate: rowGridTemplateOverride,
  rowColumnGap,
}: ResultsFileTestExecutionInlineListProps): JSX.Element | null => {
  const muiTheme = useTheme();
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
  const [isExpandAllActive, setIsExpandAllActive] = useState(false);
  const toggleTestDetails = (id: string, expanded: boolean): void => {
    setExpandedTests((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };
  const copyPanel = useCopyPanel();
  const filters = useMemo(
    () =>
      mergeFilters(
        buildEqFilterValue('suiteExecutionId', suiteExecutionId),
        buildEqFilterValue('resultsFileId', resultsFileId),
      ),
    [resultsFileId, suiteExecutionId],
  );
  const executionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/results-file-suite',
      suiteExecutionId,
      resultsFileId ?? 'none',
    ),
    queryFn: async () => {
      const pageSize = 100;
      const firstPage = await queryTestExecutions(projectId, organisationId ?? undefined, {
        page: 1,
        pageSize,
        sorts: [{ field: 'timestamp', direction: 'desc' }],
        filters,
      });
      if (firstPage.total <= firstPage.items.length) {
        return firstPage;
      }
      const maxPage = Math.max(1, Math.ceil(firstPage.total / pageSize));
      const remainingPageNumbers = Array.from(
        { length: Math.max(0, maxPage - 1) },
        (_, index) => index + 2,
      );
      const remainingPages = await Promise.all(
        remainingPageNumbers.map(
          async (page) =>
            await queryTestExecutions(projectId, organisationId ?? undefined, {
              page,
              pageSize,
              sorts: [{ field: 'timestamp', direction: 'desc' }],
              filters,
            }),
        ),
      );
      const items = [firstPage.items, ...remainingPages.map((page) => page.items)].flat();
      return {
        ...firstPage,
        items,
      };
    },
  });
  const executions = executionsQuery.data?.items ?? [];
  const hasLatestTestChip = executions.some((execution) => execution.isLatest === true);
  const computedRowGridTemplate = useMemo(() => {
    const rows = executions.map((execution) => ({
      name: execution.testName ?? execution.name ?? 'Test Execution',
      runLabel:
        execution.runNumber !== null && execution.runNumber !== undefined
          ? `#${String(execution.runNumber)}`
          : '#-',
      statusLabel: formatStatusLabel(execution.status),
      durationLabel:
        execution.durationMs !== null && execution.durationMs !== undefined
          ? formatDuration(execution.durationMs)
          : '-',
    }));
    return buildExecutionMetadataGridTemplate({
      theme: muiTheme,
      rows,
      hasLatestChip: hasLatestTestChip,
      nameTypography: 'body2',
      fallbackNameWidthCh: 24,
      fallbackStatusWidthCh: 8,
      fallbackDurationWidthCh: 9,
      namePaddingPx: 6,
      runPaddingPx: 6,
      statusPaddingPx: 12,
      durationPaddingPx: 8,
    });
  }, [executions, hasLatestTestChip, muiTheme]);
  const rowGridTemplate = useMemo(() => {
    if (!rowGridTemplateOverride) {
      return computedRowGridTemplate;
    }
    return rowGridTemplateOverride;
  }, [computedRowGridTemplate, rowGridTemplateOverride]);
  const resolvedColumnGap = rowColumnGap ?? EXECUTION_ROW_COLUMN_GAP;
  const expandableExecutionIds = useMemo(
    () =>
      executions
        .filter((execution) => buildTestDetails(execution).length > 0)
        .map((execution) => execution.id),
    [executions],
  );
  const isLoading = executionsQuery.isLoading;
  const delayedLoading = useDelayedFlag(isLoading, 200);
  const actionIconSize = s('2xl');
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: s('sm'),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    color: 'text.primary',
    border: 'none',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      fontSize: iconSizes.md,
      display: 'block',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  } as const;
  const didAutoExpandTests = useRef(false);
  useEffect(() => {
    if (didAutoExpandTests.current || executions.length !== 1 || expandedTests.size > 0) {
      return;
    }
    setExpandedTests(new Set([executions[0].id]));
    didAutoExpandTests.current = true;
  }, [executions, expandedTests.size]);
  useEffect(() => {
    if (!isExpandAllActive) return;
    setExpandedTests(new Set(expandableExecutionIds));
  }, [expandableExecutionIds, isExpandAllActive]);
  if (isLoading) {
    return delayedLoading ? (
      <Box sx={{ width: '100%', paddingLeft: disableIndent ? s('none') : s('2xl') }}>
        <DataStateSkeleton variant="flat" rows={4} padding="sm" />
      </Box>
    ) : null;
  }
  if (executions.length === 0) {
    return (
      <Surface padding="md" border>
        <Text role="body" color="textSecondary">
          No test executions for this suite
        </Text>
      </Surface>
    );
  }
  const hasExpandableRows = expandableExecutionIds.length > 0;
  const expandAllTests = (): void => {
    setIsExpandAllActive(true);
    setExpandedTests(new Set(expandableExecutionIds));
  };
  const collapseAllTests = (): void => {
    setIsExpandAllActive(false);
    setExpandedTests(new Set());
  };
  const shouldRenderControls = showExpandCollapseControls;
  const shouldRenderHeader = Boolean(heading);
  const renderExpandCollapseControls = (): JSX.Element => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
      <Tooltip title="Expand" placement="top">
        <Button
          type="button"
          onClick={expandAllTests}
          sx={actionButtonSx}
          disabled={!hasExpandableRows}
          aria-label="Expand"
        >
          <OpenInFullIcon />
        </Button>
      </Tooltip>
      <Tooltip title="Collapse" placement="top">
        <Button
          type="button"
          onClick={collapseAllTests}
          sx={actionButtonSx}
          disabled={!hasExpandableRows}
          aria-label="Collapse"
        >
          <CloseFullscreenIcon />
        </Button>
      </Tooltip>
    </Box>
  );
  return (
    <Stack
      gap="md"
      sx={{
        alignItems: 'flex-start',
        paddingLeft: disableIndent ? s('none') : s('2xl'),
        width: '100%',
      }}
    >
      {shouldRenderHeader ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text role="sectionTitle">{heading}</Text>
          {shouldRenderControls ? renderExpandCollapseControls() : null}
        </Box>
      ) : null}
      {!shouldRenderHeader && shouldRenderControls ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {renderExpandCollapseControls()}
        </Box>
      ) : null}
      {executions.map((execution: TestExecutionDto) => {
        const testName = execution.testName ?? execution.name ?? 'Test Execution';
        const runNumber =
          execution.runNumber !== null && execution.runNumber !== undefined
            ? String(execution.runNumber)
            : '-';
        const hasTestRunNumber = execution.runNumber !== null && execution.runNumber !== undefined;
        const durationLabel =
          execution.durationMs !== null && execution.durationMs !== undefined
            ? formatDuration(execution.durationMs)
            : '-';
        const testSlug = execution.testSlug ?? execution.testId;
        const testDetails = buildTestDetails(execution);
        const sharedOutputPanelWidthPx = measureOutputPanelWidthPx({
          items: testDetails,
          theme: muiTheme,
        });
        const hasTestDetails = testDetails.length > 0;
        const isExpanded = expandedTests.has(execution.id);
        const summaryContent = (
          <Box
            sx={{
              display: 'inline-grid',
              gridTemplateColumns: rowGridTemplate,
              columnGap: resolvedColumnGap,
              alignItems: 'center',
              width: 'fit-content',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Inline gap="md" wrap="nowrap" sx={{ alignItems: 'center', minWidth: 0 }}>
              {testSlug ? (
                <Link
                  component={RouterLink}
                  to={`/tests/${testSlug}`}
                  underline="hover"
                  color="inherit"
                  sx={{
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    fontWeight: (theme) => theme.typography.fontWeightRegular,
                  }}
                >
                  {testName}
                </Link>
              ) : (
                <Text
                  role="body"
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    fontWeight: (theme) => theme.typography.fontWeightRegular,
                  }}
                >
                  {testName}
                </Text>
              )}
            </Inline>
            <Inline gap="none" wrap="nowrap" sx={{ alignItems: 'center', minWidth: 0 }}>
              <Text
                role="body"
                color="textSecondary"
                component="span"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {hasTestRunNumber && testSlug ? (
                  <Link
                    component={RouterLink}
                    to={`/tests/${testSlug}/runs/${runNumber}`}
                    underline="hover"
                    color="inherit"
                    sx={{ cursor: 'pointer' }}
                  >
                    #{runNumber}
                  </Link>
                ) : (
                  `#${runNumber}`
                )}
              </Text>
            </Inline>
            <Box sx={LATEST_CHIP_COLUMN_SX}>
              {hasLatestTestChip ? (
                <Chip
                  label="Latest"
                  tone="primary"
                  transform="uppercase"
                  size="xs"
                  sx={execution.isLatest ? undefined : { visibility: 'hidden' }}
                  aria-hidden={!execution.isLatest}
                />
              ) : null}
            </Box>
            <Text
              role="body"
              component="span"
              sx={{ color: (theme) => getStatusTextColor(theme, execution.status) }}
              transform="uppercase"
            >
              {formatStatusLabel(execution.status)}
            </Text>
            <Text role="body" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              {durationLabel}
            </Text>
          </Box>
        );
        if (!hasTestDetails) {
          return (
            <Box key={execution.id} sx={{ width: '100%' }}>
              {summaryContent}
            </Box>
          );
        }
        return (
          <Box key={execution.id} sx={{ width: '100%' }}>
            <Accordion
              disableGutters
              elevation={0}
              square
              expanded={isExpanded}
              onChange={(event, expanded) => {
                const target = event.target as HTMLElement | null;
                if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
                  return;
                }
                if (!expanded) {
                  setIsExpandAllActive(false);
                }
                toggleTestDetails(execution.id, expanded);
              }}
              sx={{
                backgroundColor: 'transparent',
                '&::before': { display: 'none' },
                width: '100%',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandIcon fontSize="small" />}
                sx={{
                  padding: s('none'),
                  minHeight: 'auto',
                  justifyContent: 'flex-start',
                  width: 'fit-content',
                  maxWidth: '100%',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  cursor: 'default !important',
                  '&.MuiButtonBase-root': {
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                    cursor: 'default !important',
                  },
                  '&:hover': {
                    cursor: 'default !important',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    alignItems: 'center',
                    flexGrow: 0,
                    minWidth: 0,
                    width: 'fit-content',
                    maxWidth: '100%',
                    justifyContent: 'flex-start',
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                    cursor: 'default !important',
                  },
                  '& .MuiAccordionSummary-content *': {
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    marginLeft: s('xs'),
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                  },
                }}
              >
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  sx={{ width: '100%' }}
                >
                  {summaryContent}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: s('none'),
                  paddingTop: s('md'),
                  paddingBottom: s('sm'),
                  width: '100%',
                }}
              >
                <Stack gap="lg">
                  {testDetails
                    .filter((item) => item.label.startsWith('Failure'))
                    .map((item) => (
                      <ResultsFileOutputPanel
                        key={item.label}
                        items={[item]}
                        onCopy={copyPanel}
                        sharedWidthPx={sharedOutputPanelWidthPx}
                      />
                    ))}
                  <ResultsFileOutputPanel
                    items={testDetails.filter((item) => !item.label.startsWith('Failure'))}
                    onCopy={copyPanel}
                    sharedWidthPx={sharedOutputPanelWidthPx}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
    </Stack>
  );
};
